import type { Appearance } from '@/api/generated/schemas.ts';

// What someone can pick for how Recap looks (plan task 120), from the options tried in experiments/colors: themes,
// layouts of the rail, the top bar and the page, and fonts for headlines. Every value is a token of index.css, by its
// name without `--color-`.

export type Tokens = Record<string, string>;
export type AppearanceTheme = Appearance['theme'];
export type AppearanceLayout = Appearance['layout'];
export type HeadlineFont = Appearance['headline_font'];

const mix = (color: string, percent: number, base: string) => `color-mix(in srgb, ${color} ${percent}%, ${base})`;

const neutrals = (
    surface: string,
    muted: string,
    hover: string,
    border: string,
    text: string,
    textMuted: string,
    textSubtle: string
): Tokens => ({
    surface,
    'surface-muted': muted,
    'surface-hover': hover,
    'surface-sunken': muted,
    border,
    text,
    'text-muted': textMuted,
    'text-subtle': textSubtle,
});

// index.css's, so a layout can mix from them.
const TODAY_LIGHT: Tokens = {
    ...neutrals('#ffffff', '#f4f4f4', '#ececec', '#ebebeb', '#0a0a0a', '#7c7c7c', '#b3b3b3'),
    'surface-sunken': '#f2f2f2',
    'border-shaded': '#0000000d',
    primary: '#0a0a0a',
    'primary-hover': '#2a2a2a',
    'on-primary': '#ffffff',
};
const TODAY_DARK: Tokens = {
    ...neutrals('#151515', '#1f1f1f', '#292929', '#262626', '#f2f2f2', '#8e8e8e', '#5a5a5a'),
    'surface-sunken': '#0d0d0d',
    'border-shaded': '#ffffff0d',
    primary: '#f2f2f2',
    'primary-hover': '#d6d6d6',
    'on-primary': '#0a0a0a',
};

const WARM_PAPER = neutrals('#fefdfb', '#f6f3ed', '#eeeae2', '#eeeae2', '#1a1714', '#78716a', '#b8b1a7');
const STONE = neutrals('#fcfcfb', '#f3f2f0', '#e9e7e4', '#e8e6e3', '#1c1917', '#736d68', '#b3aea9');
const MIST = neutrals('#fbfcfd', '#f1f4f7', '#e7ecf1', '#e6ebf0', '#0f1720', '#687480', '#aeb7c0');
const WARM_DARK = neutrals('#1c1a17', '#262420', '#312e29', '#312e29', '#f0ece3', '#9c968a', '#5f5a51');
const COOL_DARK = neutrals('#14171c', '#1d2128', '#272c34', '#252a31', '#eef1f5', '#8d96a1', '#555d67');

interface Colors {
    primary: string;
    hover: string;
    onPrimary: string;
    // What shows where one is in time and on a page (today, the tab's line); the primary unless the theme has one of
    // its own.
    accent?: string;
    // The accent as text, darker where the accent is too light to read.
    accentText?: string;
}

// A theme's tokens from its neutrals and colours: the accent marks today and the tab, while the rail's current item is
// a stronger step of its hover in the text's colour, as the rail stays quiet.
const colored = (base: Tokens, colors: Colors, extra: Tokens = {}): Tokens => {
    const accent = colors.accent ?? colors.primary;
    const accentText = colors.accentText ?? accent;
    return {
        ...base,
        'border-shaded': base['border-shaded'] ?? '#0000000c',
        primary: colors.primary,
        'primary-hover': colors.hover,
        'on-primary': colors.onPrimary,
        accent,
        'nav-active': base['surface-hover'],
        'nav-active-text': base.text,
        today: accent,
        'today-text': colors.onPrimary,
        'tab-line': accent,
        'bar-current': accent,
        eyebrow: accentText,
        link: accentText,
        ...extra,
    };
};

// With an orange theme, the user's own orange („du“) turns violet, so the two aren't taken for each other.
const SELF_LIGHT = { self: '#7c4dcc' };
const SELF_DARK = { self: '#b394f0' };

export interface Theme {
    light: Tokens;
    dark: Tokens;
}

export const THEMES: Record<AppearanceTheme, Theme> = {
    classic: {
        light: {
            ...TODAY_LIGHT,
            accent: '#0a0a0a',
            'nav-active': '#ececec',
            'nav-active-text': '#0a0a0a',
            today: '#0a0a0a',
            'today-text': '#ffffff',
            'tab-line': '#0a0a0a',
            'bar-current': '#0a0a0a',
            eyebrow: '#b3b3b3',
        },
        dark: {
            ...TODAY_DARK,
            accent: '#f2f2f2',
            'nav-active': '#292929',
            'nav-active-text': '#f2f2f2',
            today: '#f2f2f2',
            'today-text': '#151515',
            'tab-line': '#f2f2f2',
            'bar-current': '#f2f2f2',
            eyebrow: '#5a5a5a',
        },
    },
    terracotta: {
        // A warm cream for the tinted rail, as in experiments/colors (4F4), whose items are hovered with a stronger warm
        // gray and the current one with a stronger step of it: the terracotta's own tint read pink.
        light: colored(
            WARM_PAPER,
            { primary: '#b5532f', hover: '#9c4627', onPrimary: '#ffffff', accentText: '#a34a29' },
            {
                ...SELF_LIGHT,
                'rail-tint': '#faf8f3',
                'nav-hover': '#f4f0e8',
                'nav-active': '#efeae1',
            }
        ),
        dark: colored(
            WARM_DARK,
            { primary: '#e8946f', hover: '#f0ab8b', onPrimary: '#1f0e07' },
            {
                ...SELF_DARK,
                'rail-tint': '#201d1a',
                'nav-hover': '#282521',
                'nav-active': '#2d2823',
            }
        ),
    },
    graphite: {
        light: colored(STONE, {
            primary: '#2b2b2e',
            hover: '#3d3d41',
            onPrimary: '#ffffff',
            accent: '#b5703c',
            accentText: '#9a5a2e',
        }),
        dark: colored(WARM_DARK, { primary: '#ecebe8', hover: '#d6d4cf', onPrimary: '#1c1917', accent: '#e0a477' }),
    },
    navy: {
        light: colored(MIST, { primary: '#1d3557', hover: '#152a47', onPrimary: '#ffffff' }),
        dark: colored(COOL_DARK, { primary: '#8fb0dc', hover: '#a9c3e6', onPrimary: '#0b1424' }),
    },
    slate: {
        light: colored(MIST, { primary: '#3d5a73', hover: '#324b60', onPrimary: '#ffffff', accentText: '#36516a' }),
        dark: colored(COOL_DARK, { primary: '#9fbbd2', hover: '#b6cbdd', onPrimary: '#0c151d' }),
    },
    indigo: {
        light: colored(STONE, { primary: '#3d3aa8', hover: '#322f8c', onPrimary: '#ffffff' }),
        dark: colored(WARM_DARK, { primary: '#a5a3f5', hover: '#bdbcf8', onPrimary: '#0e0d2b' }),
    },
};

// A step away from the surface towards the text: darker in the light theme, lighter in the dark one.
const step = (tokens: Tokens, percent: number) => mix(tokens.text, percent, tokens.surface);
// Lighter than the page: white, or in the dark theme a step lighter.
const lifted = (tokens: Tokens, isDark: boolean) => (isDark ? step(tokens, 7) : '#ffffff');
// A darker page under light cards; its controls a step darker too, so they don't vanish into it.
const darkerPage = (tokens: Tokens, percent: number) => ({
    surface: step(tokens, percent),
    'surface-muted': step(tokens, percent + 4),
    'surface-hover': step(tokens, percent + 8),
    border: step(tokens, percent + 6),
});

// A rail lighter than the theme's gray, so its items can be hovered with a gentle step darker than it and the current
// one with a stronger step, as Terrakotta's (`rail-tint`, which brings its own steps): `share` of the way from the page
// to the gray, with a trace of the theme's accent.
const railed = (tokens: Tokens, share: number): Tokens => {
    if (tokens['rail-tint']) return { rail: tokens['rail-tint'] };
    const gray = mix(tokens['surface-muted'], share, tokens.surface);
    // Klassisch's accent is its text, which would only gray the rail.
    const rail = tokens.accent === tokens.text ? gray : mix(tokens.accent, 2, gray);
    return { rail, 'nav-hover': mix(tokens.text, 3, rail), 'nav-active': mix(tokens.text, 5.5, rail) };
};

export interface Layout {
    // What it changes in a theme's tokens.
    tokens: (tokens: Tokens, isDark: boolean) => Tokens;
    // How AppLayout arranges the rail and the page: the page as a sheet set into the rail's surface, or the rail and
    // the top bar dark (`chrome-dark`).
    shell?: 'inset' | 'dark-frame';
}

export const LAYOUTS: Record<AppearanceLayout, Layout> = {
    flat: { tokens: () => ({}) },
    // The rail light, halfway to the theme's gray; around the inset page a little deeper, so the page stands out.
    tinted_rail: { tokens: (tokens) => railed(tokens, 50) },
    canvas: {
        tokens: (tokens, isDark) => ({
            ...darkerPage(tokens, 4),
            rail: lifted(tokens, isDark),
            top: lifted(tokens, isDark),
            'surface-shaded': lifted(tokens, isDark),
        }),
    },
    inset: {
        tokens: (tokens) => railed(tokens, 80),
        shell: 'inset',
    },
    dark_frame: {
        tokens: (tokens, isDark) => {
            const chrome = isDark ? mix('#000000', 45, tokens.surface) : mix(tokens.text, 92, tokens.accent);
            return {
                'chrome-dark': chrome,
                'nav-hover': mix('#ffffff', 6, chrome),
                'nav-active': mix('#ffffff', 9, chrome),
                'nav-active-text': '#f4f2ee',
            };
        },
        shell: 'dark-frame',
    },
};

export interface Font {
    family: string;
    // Loads the font's files once it's picked, so only the one in use is downloaded.
    load?: () => Promise<unknown>;
}

// Self-hosted, as Google's servers would learn every visitor's address (plan task 120).
export const FONTS: Record<HeadlineFont, Font> = {
    inter: { family: "'Inter Variable', ui-sans-serif, system-ui, sans-serif" },
    fraunces: {
        family: "'Fraunces Variable', Georgia, serif",
        load: () => import('@fontsource-variable/fraunces'),
    },
    newsreader: {
        family: "'Newsreader Variable', Georgia, serif",
        load: () => import('@fontsource-variable/newsreader'),
    },
    hanken_grotesk: {
        family: "'Hanken Grotesk Variable', ui-sans-serif, sans-serif",
        load: () => import('@fontsource-variable/hanken-grotesk'),
    },
    bricolage_grotesque: {
        family: "'Bricolage Grotesque Variable', ui-sans-serif, sans-serif",
        load: () => import('@fontsource-variable/bricolage-grotesque'),
    },
};

// The colours someone can change one by one, in the order the settings show them; the API's names, with underscores.
export const APPEARANCE_COLORS = [
    'primary',
    'on_primary',
    'surface',
    'surface_shaded',
    'surface_muted',
    'border',
    'text',
    'text_muted',
    'rail',
    'top',
    'nav_hover',
    'nav_active',
    'nav_active_text',
    'today',
    'today_text',
    'eyebrow',
    'tab_line',
    'bar_current',
    'link',
] as const;
export type AppearanceColor = (typeof APPEARANCE_COLORS)[number] | 'chrome_dark';

// A colour's token name (`nav-active`) from its name in the API (`nav_active`).
export const tokenName = (color: string) => color.replaceAll('_', '-');

export const DEFAULT_APPEARANCE: Appearance = {
    theme: 'classic',
    layout: 'flat',
    headline_font: 'inter',
    colors_light: {},
    colors_dark: {},
    is_dark_derivation_enabled: true,
};
